import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TablePagination
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../api";
import logger from "../utils/logger";
import { useAuth } from "../context/AuthContext";

const UploadedFilesList = ({ showAllFiles = false }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use list_all_files for admins, list_files for supervisors (their own files)
      const endpoint = showAllFiles ? '/api/files/list_all_files' : '/api/files/list_files';
      const response = await api.get(endpoint);
      
      if (response.data.success) {
        // Normalize file list - ensure all files have user and record_count fields
        const normalizedFiles = (response.data.files || []).map(file => ({
          ...file,
          user: file.user || null,
          record_count: file.record_count || 0
        }));
        setFiles(normalizedFiles);
      } else {
        setError(response.data.message || 'Failed to fetch files');
      }
    } catch (err) {
      logger.error('Error fetching files:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (file) => {
    setSelectedFile(file);
    setPreviewDialogOpen(true);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(true);

    try {
      const response = await api.get(`/api/files/get_file_preview/${file.id}`);
      
      if (response.data.success) {
        setPreviewData(response.data);
      } else {
        setPreviewError(response.data.message || 'Failed to load file preview');
      }
    } catch (err) {
      logger.error('Error fetching file preview:', err);
      setPreviewError(err.response?.data?.message || err.message || 'Failed to load file preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/api/files/get_file/${fileId}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ mt: 3, px: 3, pb: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Uploaded Files
          </Typography>
          <Button
            variant="outlined"
            onClick={fetchFiles}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Alert severity="info">No files uploaded yet</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Filename</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell>Upload Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((file) => (
                    <TableRow key={file.id} hover>
                      <TableCell>{file.filename}</TableCell>
                      <TableCell>
                        <Chip 
                          label={file.file_type.toUpperCase()} 
                          size="small"
                          color={file.file_type === 'excel' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {file.user ? file.user.username : 'Unknown'}
                      </TableCell>
                      <TableCell align="right">{file.record_count || 0}</TableCell>
                      <TableCell>{formatDate(file.created_at)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Preview file contents">
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(file)}
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download file">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file.id, file.filename)}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={files.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          File Preview: {selectedFile?.filename}
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : previewError ? (
            <Alert severity="error">{previewError}</Alert>
          ) : previewData ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Rows:</strong> {previewData.total_rows} | 
                  <strong> Total Columns:</strong> {previewData.total_columns} | 
                  <strong> Preview Rows:</strong> {previewData.preview_rows}
                  {previewData.columns_truncated > 0 && (
                    <span> | <strong>Columns Truncated:</strong> {previewData.columns_truncated}</span>
                  )}
                </Typography>
              </Box>
              
              {previewData.data && previewData.data.length > 0 ? (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {previewData.columns.map((col, idx) => (
                          <TableCell key={idx} sx={{ fontWeight: 'bold', backgroundColor: 'background.paper' }}>
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.data.map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          {previewData.columns.map((col, colIdx) => (
                            <TableCell key={colIdx}>
                              {row[col] !== null && row[col] !== undefined ? String(row[col]) : ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No data to display</Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadedFilesList;
